import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { api, getToken } from "./lib/api";
import type {
  AdminPlatformData,
  AgencyApplication,
  AgencyEvent,
  EventRegistration,
  HomepageContent,
  Settings,
  TalentProfile,
  TalentNotification,
  User,
} from "./types";
import { EventsPage, OpportunitiesPage, PhaseTwoAdmin } from "./PhaseTwo";
import { PhaseThreeAdmin, PortfolioEditor, TalentEngagement } from "./PhaseThree";

const FALLBACK_HOME: HomepageContent = {
  eyebrow: "West Columbia, South Carolina",
  title: "Where talent meets opportunity.",
  intro:
    "Discover your presence. Develop your craft. Step confidently into fashion, film, theater, and performance.",
  primary_cta: "Join the agency",
  secondary_cta: "Explore talent",
};

const offers = [
  [
    "01",
    "Professional photoshoots",
    "Build a polished portfolio with editorial direction and industry-ready imagery.",
  ],
  [
    "02",
    "Runway & pageant training",
    "Develop poise, movement, presentation, and confidence for the stage.",
  ],
  [
    "03",
    "Casting opportunities",
    "Prepare for real calls, showcases, productions, and professional exposure.",
  ],
  [
    "04",
    "Mentorship that lasts",
    "Grow with personal guidance in a supportive, family-oriented environment.",
  ],
];

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function route() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}
function money(cents: number, currency = "usd") {
  return cents
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        maximumFractionDigits: cents % 100 ? 2 : 0,
      }).format(cents / 100)
    : "Pricing coming soon";
}
function dateLabel(value: string) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(date)
    : "Date to be announced";
}
function splitList(value: string) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
function isAdmin(user: User | null) {
  return ["admin", "owner", "site_owner"].includes(
    String(user?.role || "").toLowerCase(),
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <button
      className={`wordmark ${compact ? "wordmark-compact" : ""}`}
      onClick={() => navigate("/")}
      aria-label="Paris Runway Elite home"
    >
      <span className="wordmark-paris">PARIS</span>
      <span>RUNWAY ELITE</span>
    </button>
  );
}

function SiteHeader({ user }: { user: User | null }) {
  return (
    <header className="site-header public-header">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <Brand />
      <nav className="desktop-nav" aria-label="Primary navigation">
        <button onClick={() => navigate("/talent")}>Talent</button>
        <button onClick={() => navigate("/events")}>Events</button>
        <button onClick={() => navigate("/opportunities")}>
          Opportunities
        </button>
        <button onClick={() => navigate("/#contact")}>Contact</button>
      </nav>
      <button
        className="header-cta"
        onClick={() =>
          navigate(user ? (isAdmin(user) ? "/admin" : "/dashboard") : "/login")
        }
      >
        {user ? "My workspace" : "Sign in"} <span>↗</span>
      </button>
    </header>
  );
}

function AppShell({
  user,
  title,
  eyebrow,
  children,
  active,
  onLogout,
}: {
  user: User;
  title: string;
  eyebrow: string;
  children: ReactNode;
  active: "dashboard" | "admin";
  onLogout: () => void;
}) {
  return (
    <div className="workspace-shell">
      <aside className="workspace-sidebar">
        <Brand compact />
        <nav>
          {!isAdmin(user) && (
            <button
              className={active === "dashboard" ? "active" : ""}
              onClick={() => navigate("/dashboard")}
            >
              Talent workspace
            </button>
          )}
          {isAdmin(user) && (
            <button
              className={active === "admin" ? "active" : ""}
              onClick={() => navigate("/admin")}
            >
              Agency administration
            </button>
          )}
          <button onClick={() => navigate("/talent")}>Public directory</button>
          <button onClick={() => navigate("/")}>View website</button>
        </nav>
        <div className="workspace-user">
          <span>{user.name || user.email}</span>
          <small>{user.role.replace("_", " ")}</small>
          <button onClick={onLogout}>Sign out</button>
        </div>
      </aside>
      <main className="workspace-main">
        <header className="workspace-header">
          <div>
            <p className="workspace-eyebrow">{eyebrow}</p>
            <h1>{title}</h1>
          </div>
          <button className="workspace-view-site" onClick={() => navigate("/")}>
            View live site ↗
          </button>
        </header>
        {children}
      </main>
    </div>
  );
}

function Status({ value }: { value: string }) {
  return (
    <span className={`status status-${value.replace(/\s|_/g, "-")}`}>
      {value.replace(/_/g, " ")}
    </span>
  );
}
function Loading() {
  return (
    <div className="loading-screen">
      <Brand />
      <span>Preparing your spotlight…</span>
    </div>
  );
}
function Empty({ title, copy }: { title: string; copy: string }) {
  return (
    <div className="empty-state">
      <span>✦</span>
      <h3>{title}</h3>
      <p>{copy}</p>
    </div>
  );
}

function Home({ user }: { user: User | null }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [profiles, setProfiles] = useState<TalentProfile[]>([]);
  const [events, setEvents] = useState<AgencyEvent[]>([]);
  useEffect(() => {
    api
      .publicTalent()
      .then((result) => {
        setSettings(result.settings);
        setProfiles(result.profiles);
        setEvents(result.events);
      })
      .catch(() => undefined);
  }, []);
  const home = { ...FALLBACK_HOME, ...(settings?.homepage || {}) };
  const parts = String(home.title || FALLBACK_HOME.title).split(
    /\s+(?=[^ ]+$)/,
  );
  useEffect(() => {
    if (window.location.hash)
      setTimeout(
        () =>
          document
            .querySelector(window.location.hash)
            ?.scrollIntoView({ behavior: "smooth" }),
        100,
      );
  }, []);
  return (
    <main>
      <SiteHeader user={user} />
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-shade" />
        <div className="hero-content">
          <p className="eyebrow hero-reveal">{home.eyebrow}</p>
          <h1 id="hero-title" className="hero-reveal">
            {parts.slice(0, -1).join(" ")}
            <br />
            <em>{parts.at(-1)}</em>
          </h1>
          <p className="hero-copy hero-reveal">{home.intro}</p>
          <div className="hero-actions hero-reveal">
            <button
              className="button button-gold"
              onClick={() => navigate(user ? "/dashboard" : "/apply")}
            >
              {home.primary_cta} <span>↗</span>
            </button>
            <button className="text-link" onClick={() => navigate("/talent")}>
              {home.secondary_cta} <span>→</span>
            </button>
          </div>
        </div>
        <div className="hero-rail">
          <span>Models</span>
          <span>Actors</span>
          <span>Mentorship</span>
          <span>Opportunity</span>
        </div>
      </section>
      <section className="manifesto section-pad" id="about">
        <div className="section-index">01 / Who we are</div>
        <div className="manifesto-copy reveal">
          <p className="eyebrow">Confidence is the beginning</p>
          <h2>
            We don&apos;t just create talent.
            <br />
            <em>We build leaders, professionals, and stars.</em>
          </h2>
          <p className="large-copy">
            Paris Runway Elite discovers, trains, and showcases emerging talent
            across fashion, film, theater, and the performance arts.
          </p>
        </div>
      </section>
      <section className="offer section-pad">
        <div className="offer-heading reveal">
          <div>
            <p className="eyebrow">What we offer</p>
            <h2>Preparation for the moment that matters.</h2>
          </div>
          <p>
            From the first walk to the final callback, our training brings
            technique and self-belief together.
          </p>
        </div>
        <div className="offer-list">
          {offers.map(([number, title, copy]) => (
            <article className="offer-row reveal" key={number}>
              <span className="offer-number">{number}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
              <span className="row-arrow">↗</span>
            </article>
          ))}
        </div>
      </section>
      <section className="directory-tease section-pad">
        <div className="directory-tease-head reveal">
          <div>
            <p className="eyebrow">Represented talent</p>
            <h2>Ready for the next call.</h2>
          </div>
          <button className="text-link" onClick={() => navigate("/talent")}>
            View the directory <span>↗</span>
          </button>
        </div>
        {profiles.length ? (
          <div className="talent-filmstrip">
            {profiles.slice(0, 4).map((profile) => (
              <TalentPortrait
                key={profile.user_id}
                profile={profile}
                onClick={() => navigate(`/talent/${profile.public_slug}`)}
              />
            ))}
          </div>
        ) : (
          <Empty
            title="The first profiles are being prepared"
            copy="Approved members will appear here when their portfolios and memberships are active."
          />
        )}
      </section>
      <section className="agency-calendar section-pad" id="calendar">
        <div className="calendar-heading">
          <p className="eyebrow">Agency calendar</p>
          <h2>Train. Audition. Perform.</h2>
          <button className="text-link" onClick={() => navigate("/events")}>
            Explore all events <span>↗</span>
          </button>
        </div>
        {events.length ? (
          <div className="calendar-list">
            {events.slice(0, 5).map((event) => (
              <button
                className="calendar-row"
                key={event.id}
                onClick={() => navigate(`/events/${event.id}`)}
              >
                <time>{dateLabel(event.starts_at)}</time>
                <div>
                  <h3>{event.title}</h3>
                  <p>
                    {event.location || "Location shared with registered talent"}
                  </p>
                </div>
                <span>{event.audience} ↗</span>
              </button>
            ))}
          </div>
        ) : (
          <Empty
            title="New dates are on the way"
            copy="Upcoming training, auditions, and showcases will be published here by the agency."
          />
        )}
      </section>
      <section className="contact section-pad" id="contact">
        <div className="contact-copy reveal">
          <p className="eyebrow">Join the movement</p>
          <h2>
            Your journey.
            <br />
            Your stage.
            <br />
            <em>Your story.</em>
          </h2>
        </div>
        <div className="contact-details reveal">
          <p className="contact-lead">
            Create your talent account, build your profile, and prepare for your
            next opportunity.
          </p>
          <button
            className="button button-gold"
            onClick={() => navigate(user ? "/dashboard" : "/apply")}
          >
            Create a talent profile <span>↗</span>
          </button>
          <div className="contact-grid">
            <div>
              <span>Email</span>
              <a href="mailto:parisrunwayelitesp@gmail.com">
                parisrunwayelitesp@gmail.com
              </a>
            </div>
            <div>
              <span>Call</span>
              <a href="tel:+18032001633">803-200-1633</a>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function TalentPortrait({
  profile,
  onClick,
}: {
  profile: TalentProfile;
  onClick: () => void;
}) {
  return (
    <button className="talent-portrait" onClick={onClick}>
      <div
        className="portrait-image"
        style={
          profile.profile_photo_url
            ? { backgroundImage: `url(${profile.profile_photo_url})` }
            : undefined
        }
      >
        <span>{profile.stage_name.slice(0, 1)}</span>
      </div>
      <div>
        <small>{profile.talent_type}</small>
        <h3>{profile.stage_name}</h3>
        <p>
          {[profile.city, profile.state].filter(Boolean).join(", ") ||
            "Paris Runway Elite"}
        </p>
      </div>
    </button>
  );
}

function Directory({ user }: { user: User | null }) {
  const [profiles, setProfiles] = useState<TalentProfile[]>([]);
  const [filter, setFilter] = useState<"all" | "model" | "actor">("all");
  const [query, setQuery] = useState("");
  useEffect(() => {
    api
      .publicTalent()
      .then((result) => setProfiles(result.profiles))
      .catch(() => undefined);
  }, []);
  const shown = profiles.filter(
    (profile) =>
      (filter === "all" || profile.talent_type === filter) &&
      `${profile.stage_name} ${profile.city} ${profile.skills.join(" ")}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  return (
    <main className="directory-page">
      <SiteHeader user={user} />
      <section className="directory-hero">
        <p className="eyebrow">Paris Runway Elite roster</p>
        <h1>
          Find your <em>next face.</em>
        </h1>
        <p>
          Explore agency-approved Models and Actors with active professional
          profiles.
        </p>
      </section>
      <section className="directory-controls">
        <div>
          {(["all", "model", "actor"] as const).map((item) => (
            <button
              className={filter === item ? "active" : ""}
              key={item}
              onClick={() => setFilter(item)}
            >
              {item === "all" ? "All talent" : `${item}s`}
            </button>
          ))}
        </div>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search name, city, or skill"
          aria-label="Search talent"
        />
      </section>
      <section className="directory-grid">
        {shown.length ? (
          shown.map((profile) => (
            <TalentPortrait
              key={profile.user_id}
              profile={profile}
              onClick={() => navigate(`/talent/${profile.public_slug}`)}
            />
          ))
        ) : (
          <Empty
            title="No profiles match yet"
            copy="Try another search, or check back as new talent is approved."
          />
        )}
      </section>
      <Footer />
    </main>
  );
}

function PublicProfile({ user, slug }: { user: User | null; slug: string }) {
  const [profile, setProfile] = useState<TalentProfile | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    api
      .publicTalent(slug)
      .then((result) => setProfile(result.profiles[0] || null))
      .finally(() => setReady(true));
  }, [slug]);
  if (!ready) return <Loading />;
  if (!profile)
    return (
      <main className="directory-page">
        <SiteHeader user={user} />
        <div className="not-found">
          <h1>Profile unavailable.</h1>
          <p>This profile may still be under review or no longer public.</p>
          <button
            className="button button-gold"
            onClick={() => navigate("/talent")}
          >
            Back to talent
          </button>
        </div>
      </main>
    );
  return (
    <main className="profile-page">
      <SiteHeader user={user} />
      <section className="profile-hero">
        <div
          className="profile-photo"
          style={
            profile.profile_photo_url
              ? { backgroundImage: `url(${profile.profile_photo_url})` }
              : undefined
          }
        >
          <span>{profile.stage_name.slice(0, 1)}</span>
        </div>
        <div className="profile-intro">
          <p className="eyebrow">{profile.talent_type} · Paris Runway Elite</p>
          <h1>{profile.stage_name}</h1>
          <p className="profile-headline">
            {profile.headline || "Available for professional opportunities."}
          </p>
          <p>{[profile.city, profile.state].filter(Boolean).join(", ")}</p>
          <a
            className="button button-gold"
            href={`mailto:parisrunwayelitesp@gmail.com?subject=Booking%20inquiry%20for%20${encodeURIComponent(profile.stage_name)}`}
          >
            Request this talent <span>↗</span>
          </a>
        </div>
      </section>
      {profile.gallery.length > 0 && (
        <section className="profile-gallery" aria-label={`${profile.stage_name} portfolio gallery`}>
          {profile.gallery.map((image, index) => (
            <figure key={image}>
              <img src={image} alt={`${profile.stage_name} portfolio ${index + 1}`} loading="lazy" />
            </figure>
          ))}
        </section>
      )}
      <section className="profile-details section-pad">
        <div>
          <p className="eyebrow">Profile</p>
          <h2>Presence with purpose.</h2>
        </div>
        <div>
          <p className="profile-bio">
            {profile.bio ||
              profile.experience ||
              "Portfolio details available through the agency."}
          </p>
          {profile.skills.length > 0 && (
            <div className="skill-list">
              {profile.skills.map((skill) => (
                <span key={skill}>{skill}</span>
              ))}
            </div>
          )}
          <dl>
            {Object.entries(profile.measurements)
              .filter(([, value]) => value)
              .map(([key, value]) => (
                <div key={key}>
                  <dt>{key.replace(/_/g, " ")}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
          </dl>
          {profile.reel_url && (
            <a
              className="text-link"
              href={profile.reel_url}
              target="_blank"
              rel="noreferrer"
            >
              View reel <span>↗</span>
            </a>
          )}
          {profile.reels.map((reel, index) => (
            <a className="text-link profile-media-link" href={reel} target="_blank" rel="noreferrer" key={reel}>Reel {index + 1} <span>↗</span></a>
          ))}
          {profile.resume_url && <a className="text-link profile-media-link" href={profile.resume_url} target="_blank" rel="noreferrer">Download résumé <span>↓</span></a>}
        </div>
      </section>
      <Footer />
    </main>
  );
}

function AuthPage({ onAuth }: { onAuth: (user: User) => void }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await api.login(form.email, form.password);
      onAuth(user);
      navigate(isAdmin(user) ? "/admin" : "/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to continue.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-page">
      <button className="auth-back" onClick={() => navigate("/")}>
        ← Back to site
      </button>
      <section className="auth-image">
        <Brand />
        <div>
          <p className="eyebrow">Welcome back</p>
          <h1>
            Your next move
            <br />
            <em>starts here.</em>
          </h1>
        </div>
      </section>
      <section className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit}>
          <p className="workspace-eyebrow">Secure talent portal</p>
          <h2>Sign in</h2>
          <p>Approved members can sign in after their membership payment is confirmed.</p>
          <label>
            Email address
            <input
              required
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
              autoComplete="email"
            />
          </label>
          <label>
            Password
            <input
              required
              type="password"
              minLength={8}
              value={form.password}
              onChange={(event) =>
                setForm({ ...form, password: event.target.value })
              }
              autoComplete="current-password"
            />
          </label>
          {error && <div className="form-alert">{error}</div>}
          <button className="button button-dark" disabled={busy}>
            {busy ? "Please wait…" : "Sign in"}
          </button>
          <p className="auth-recovery">
            <button type="button" onClick={() => navigate("/forgot-password")}>Forgot your password?</button>
          </p>
          <p className="auth-switch">
            New to the agency?{" "}
            <button type="button" onClick={() => navigate("/apply")}>Submit an application</button>
          </p>
        </form>
      </section>
    </main>
  );
}

function PasswordRecoveryPage({ reset = false }: { reset?: boolean }) {
  const token = new URLSearchParams(window.location.search).get("token")?.trim() || "";
  const [email, setEmail] = useState("");
  const [passwords, setPasswords] = useState({ password: "", confirm: "" });
  const [error, setError] = useState(reset && !token ? "This reset link is incomplete. Please request a new one." : "");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError("");
    setNotice("");

    if (reset) {
      if (!token) return setError("This reset link is incomplete. Please request a new one.");
      if (passwords.password.length < 8) return setError("Password must be at least 8 characters.");
      if (passwords.password !== passwords.confirm) return setError("Passwords do not match.");
    }

    setBusy(true);
    try {
      if (reset) {
        await api.resetPassword(token, passwords.password);
        setNotice("Your password has been updated. You can sign in now.");
      } else {
        await api.requestPasswordReset(email);
        setNotice("If that email belongs to an account, a secure reset link has been sent.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to continue. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <button className="auth-back" onClick={() => navigate("/login")}>← Back to sign in</button>
      <section className="auth-image">
        <Brand />
        <div>
          <p className="eyebrow">Secure account access</p>
          <h1>{reset ? <>Choose a new<br /><em>password.</em></> : <>Return to your<br /><em>spotlight.</em></>}</h1>
        </div>
      </section>
      <section className="auth-form-wrap">
        <form className="auth-form auth-recovery-form" onSubmit={submit}>
          <p className="workspace-eyebrow">Paris Runway Elite portal</p>
          <h2>{reset ? "Reset password" : "Forgot password"}</h2>
          <p>{reset ? "Create a new password for your account." : "Enter the email you use to sign in and we’ll send a secure reset link."}</p>
          {reset ? (
            <>
              <label>New password<input required type="password" minLength={8} autoComplete="new-password" value={passwords.password} onChange={(event) => setPasswords({ ...passwords, password: event.target.value })} disabled={!token || busy} /></label>
              <label>Confirm new password<input required type="password" minLength={8} autoComplete="new-password" value={passwords.confirm} onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} disabled={!token || busy} /></label>
            </>
          ) : (
            <label>Email address<input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} /></label>
          )}
          {error && <div className="form-alert" role="alert">{error}</div>}
          {notice && <div className="form-alert success" role="status">{notice}</div>}
          <button className="button button-dark" disabled={busy || (reset && !token)}>{busy ? "Please wait…" : reset ? "Update password" : "Send reset link"}</button>
          <p className="auth-switch"><button type="button" onClick={() => navigate("/login")}>Back to sign in</button></p>
        </form>
      </section>
    </main>
  );
}

function AgencyApplicationPage({ onAuth }: { onAuth: (user: User) => void }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [application, setApplication] = useState<Awaited<ReturnType<typeof api.agencyApplication>> | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    if (!token) return;
    setError("");
    try {
      setApplication(await api.agencyApplication(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load this application.");
    }
  }

  useEffect(() => {
    if (!token) return;
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (checkout === "success" && sessionId) {
      setBusy(true);
      api
        .agencyApplicationAction("confirm", token, { session_id: sessionId })
        .then((result) => {
          if (!result.paid) throw new Error("Stripe is still confirming the payment. Refresh this page in a moment.");
          setNotice("Payment confirmed. Create your password to activate your account.");
          window.history.replaceState({}, "", `/apply?token=${encodeURIComponent(token)}`);
          return load();
        })
        .catch((err) => setError(err instanceof Error ? err.message : "Unable to confirm payment."))
        .finally(() => setBusy(false));
      return;
    }
    if (checkout === "cancelled") {
      setNotice("Payment was cancelled. Your approval remains active, and you can continue when ready.");
      window.history.replaceState({}, "", `/apply?token=${encodeURIComponent(token)}`);
    }
    load();
  }, [token]);

  async function submitApplication(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const form = new FormData(event.currentTarget);
      await api.applyAgency(Object.fromEntries(form.entries()));
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to submit your application.");
    } finally {
      setBusy(false);
    }
  }

  async function startPayment() {
    setBusy(true);
    setError("");
    try {
      const result = await api.agencyApplicationAction("start", token);
      if (!result.url) throw new Error("Stripe checkout is unavailable.");
      window.location.assign(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start membership payment.");
      setBusy(false);
    }
  }

  async function activate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") || "");
    const confirmPassword = String(form.get("confirm_password") || "");
    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      setBusy(false);
      return;
    }
    try {
      const result = await api.agencyApplicationAction("activate", token, { password });
      if (!result.user) throw new Error("The account could not be activated.");
      onAuth(result.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to activate your account.");
      setBusy(false);
    }
  }

  const status = application?.application.status;
  return (
    <main className="auth-page application-page">
      <button className="auth-back" onClick={() => navigate("/")}>← Back to site</button>
      <section className="auth-image">
        <Brand />
        <div>
          <p className="eyebrow">Agency application</p>
          <h1>Earn your place.<br /><em>Build your future.</em></h1>
        </div>
        <ol className="application-steps" aria-label="Application process">
          <li className={!token ? "active" : "complete"}>Apply</li>
          <li className={token && !["paid", "activated"].includes(status || "") ? "active" : token ? "complete" : ""}>Owner review</li>
          <li className={status === "paid" ? "complete" : ["approved", "payment_pending"].includes(status || "") ? "active" : ""}>Membership</li>
          <li className={status === "activated" ? "complete" : status === "paid" ? "active" : ""}>Account access</li>
        </ol>
      </section>
      <section className="auth-form-wrap application-form-wrap">
        {submitted ? (
          <div className="auth-form application-confirmation">
            <p className="workspace-eyebrow">Application submitted</p>
            <h2>The owner will review it.</h2>
            <p>No account has been created and no payment is due yet. Watch your email for the agency’s decision and next steps.</p>
            <button className="button button-dark" onClick={() => navigate("/")}>Return to the site</button>
          </div>
        ) : token ? (
          <div className="auth-form">
            <p className="workspace-eyebrow">Approved application</p>
            <h2>{application?.application.applicant_name || "Continue your application"}</h2>
            {application && <p>{application.application.talent_type === "actor" ? "Actor" : "Model"} membership · {money(application.pricing.amount, application.pricing.currency)} per month</p>}
            {notice && <div className="form-alert success">{notice}</div>}
            {error && <div className="form-alert">{error}</div>}
            {!application && !error && <p>Loading your application…</p>}
            {application && ["approved", "payment_pending"].includes(status || "") && (
              <>
                <p>Your account will not be created until Stripe confirms the first membership payment.</p>
                <button className="button button-dark" disabled={busy || !application.commerce_ready} onClick={startPayment}>
                  {busy ? "Opening Stripe…" : !application.commerce_ready ? "Payment setup is not ready" : `Pay ${money(application.pricing.amount, application.pricing.currency)} securely`}
                </button>
                {!application.commerce_ready && <small>The agency owner must finish Stripe setup and publish pricing before payment can begin.</small>}
              </>
            )}
            {application && status === "paid" && (
              <form className="activation-form" onSubmit={activate}>
                <p>Payment is confirmed. Choose the password you will use to sign in.</p>
                <label>Password<input required minLength={8} type="password" name="password" autoComplete="new-password" /></label>
                <label>Confirm password<input required minLength={8} type="password" name="confirm_password" autoComplete="new-password" /></label>
                <button className="button button-dark" disabled={busy}>{busy ? "Activating…" : "Activate my account"}</button>
              </form>
            )}
            {application && status === "activated" && <button className="button button-dark" onClick={() => navigate("/login")}>Sign in to your account</button>}
          </div>
        ) : (
          <form className="auth-form agency-application-form" onSubmit={submitApplication}>
            <p className="workspace-eyebrow">Join Paris Runway Elite</p>
            <h2>Apply for consideration</h2>
            <p>Submitting this form does not create an account or charge you. The agency owner reviews every application first.</p>
            <div className="form-grid">
              <label>Full name<input required name="applicant_name" autoComplete="name" /></label>
              <label>Email address<input required type="email" name="applicant_email" autoComplete="email" /></label>
              <label>Phone number<input name="applicant_phone" type="tel" autoComplete="tel" /></label>
              <label>Applying as<select name="talent_type" defaultValue="model"><option value="model">Model</option><option value="actor">Actor</option></select></label>
              <label>Age range<input name="age_range" placeholder="18–24, 25–34, or youth" /></label>
              <label>City<input name="city" autoComplete="address-level2" /></label>
              <label>State<input name="state" autoComplete="address-level1" /></label>
              <label>Portfolio or reel URL<input name="portfolio_url" type="url" placeholder="https://…" /></label>
              <label className="full">Experience<textarea name="experience" rows={4} placeholder="Training, productions, runway, film, theater, or related experience" /></label>
              <label className="full">Why do you want to join? <textarea required name="statement" rows={5} /></label>
            </div>
            {error && <div className="form-alert">{error}</div>}
            <button className="button button-dark" disabled={busy}>{busy ? "Submitting…" : "Submit application"}</button>
            <p className="auth-switch">Already an approved member? <button type="button" onClick={() => navigate("/login")}>Sign in</button></p>
          </form>
        )}
      </section>
    </main>
  );
}

function TalentDashboard({
  user,
  onLogout,
}: {
  user: User;
  onLogout: () => void;
}) {
  const [data, setData] = useState<{
    profile: TalentProfile | null;
    settings: Settings;
    events: AgencyEvent[];
    registrations: EventRegistration[];
    notifications: TalentNotification[];
  } | null>(null);
  const [form, setForm] = useState<Partial<TalentProfile>>({
    talent_type: "model",
    skills: [],
    specialties: [],
    measurements: {},
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .dashboard()
      .then((result) => {
        setData(result);
        setForm(
          result.profile || {
            talent_type: "model",
            stage_name: user.name || "",
            skills: [],
            specialties: [],
            measurements: {},
          },
        );
      })
      .catch((err) => setError(err.message));
  }, [user.name]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("checkout") === "success" && sessionId)
      api
        .checkout("confirm", { session_id: sessionId })
        .then(() => api.dashboard())
        .then((next) => {
          setData(next);
          setForm(
            next.profile || {
              talent_type: "model",
              skills: [],
              specialties: [],
              measurements: {},
            },
          );
          setNotice(
            "Membership activated. Your payment method and renewals are now managed securely by Stripe.",
          );
          window.history.replaceState({}, "", "/dashboard");
        })
        .catch((err) => setError(err.message));
  }, []);
  if (!data && !error) return <Loading />;
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const saved = await api.saveProfile(form);
      if (photo)
        await api.uploadProfilePhoto({
          name: saved.stage_name,
          city: saved.city,
          state: saved.state,
          bio: saved.bio,
          public_slug: saved.public_slug,
          photo,
        });
      const next = await api.dashboard();
      setData(next);
      setForm(next.profile || saved);
      setPhoto(null);
      setNotice("Profile saved and submitted for agency review.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save profile.");
    } finally {
      setBusy(false);
    }
  }
  async function openBilling() {
    setBusy(true);
    setError("");
    try {
      const result = await api.checkout("portal", {
        return_url: `${window.location.origin}/dashboard`,
      });
      if (result.url) window.location.assign(result.url);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Billing settings are unavailable.",
      );
      setBusy(false);
    }
  }
  async function talentAction(body: Record<string, unknown>, success: string) {
    setBusy(true);
    setError("");
    try {
      setData(await api.talentAction(body));
      setNotice(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update workspace.");
    } finally {
      setBusy(false);
    }
  }
  const profile = data?.profile;
  const price =
    (form.talent_type === "actor"
      ? data?.settings.pricing.actor
      : data?.settings.pricing.model) || 0;
  return (
    <AppShell
      user={user}
      title="Talent workspace"
      eyebrow="Profile, membership, and calendar"
      active="dashboard"
      onLogout={onLogout}
    >
      <div className="workspace-summary">
        <div>
          <span>Profile review</span>
          <strong>{profile?.public_status || "not started"}</strong>
        </div>
        <div>
          <span>Membership</span>
          <strong>{profile?.membership_status || "inactive"}</strong>
        </div>
        <div>
          <span>Public link</span>
          <strong>
            {profile?.public_slug
              ? `/talent/${profile.public_slug}`
              : "Created after save"}
          </strong>
        </div>
      </div>
      {notice && <div className="workspace-notice success">{notice}</div>}
      {error && <div className="workspace-notice error">{error}</div>}
      <div className="workspace-columns">
        <section className="workspace-panel profile-editor">
          <div className="panel-heading">
            <div>
              <p className="workspace-eyebrow">Professional profile</p>
              <h2>Build your profile</h2>
            </div>
            {profile && <Status value={profile.public_status} />}
          </div>
          <form className="workspace-form" onSubmit={save}>
            <div className="form-grid">
              <label>
                Talent type
                <select
                  value={form.talent_type || "model"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      talent_type: e.target.value as "model" | "actor",
                    })
                  }
                >
                  <option value="model">Model</option>
                  <option value="actor">Actor</option>
                </select>
              </label>
              <label>
                Stage or professional name
                <input
                  required
                  value={form.stage_name || ""}
                  onChange={(e) =>
                    setForm({ ...form, stage_name: e.target.value })
                  }
                />
              </label>
              <label className="full">
                Headline
                <input
                  value={form.headline || ""}
                  onChange={(e) =>
                    setForm({ ...form, headline: e.target.value })
                  }
                  placeholder="Commercial actor · Voice talent · Columbia, SC"
                />
              </label>
              <label>
                City
                <input
                  value={form.city || ""}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </label>
              <label>
                State
                <input
                  value={form.state || ""}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </label>
              <label className="full">
                Biography
                <textarea
                  rows={5}
                  value={form.bio || ""}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                />
              </label>
              <label className="full">
                Skills <small>Separate with commas</small>
                <input
                  value={(form.skills || []).join(", ")}
                  onChange={(e) =>
                    setForm({ ...form, skills: splitList(e.target.value) })
                  }
                  placeholder="Runway, commercial, improv, dance"
                />
              </label>
              <label className="full">
                Experience
                <textarea
                  rows={4}
                  value={form.experience || ""}
                  onChange={(e) =>
                    setForm({ ...form, experience: e.target.value })
                  }
                />
              </label>
              <label>
                Height
                <input
                  value={form.measurements?.height || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      measurements: {
                        ...(form.measurements || {}),
                        height: e.target.value,
                      },
                    })
                  }
                />
              </label>
              <label>
                Representation details
                <input
                  value={form.measurements?.details || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      measurements: {
                        ...(form.measurements || {}),
                        details: e.target.value,
                      },
                    })
                  }
                  placeholder="Sizes, range, or union status"
                />
              </label>
              <label>
                Reel or portfolio URL
                <input
                  type="url"
                  value={form.reel_url || ""}
                  onChange={(e) =>
                    setForm({ ...form, reel_url: e.target.value })
                  }
                />
              </label>
              <label>
                Social URL
                <input
                  type="url"
                  value={form.social_url || ""}
                  onChange={(e) =>
                    setForm({ ...form, social_url: e.target.value })
                  }
                />
              </label>
              <label className="full">
                Profile photo
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                />
                <small>JPG, PNG, or WEBP. Maximum 5 MB.</small>
              </label>
            </div>
            <PortfolioEditor form={form} setForm={setForm} />
            <button className="workspace-primary" disabled={busy}>
              {busy ? "Saving…" : "Save and submit for review"}
            </button>
          </form>
        </section>
        <aside className="workspace-stack">
          <section className="workspace-panel membership-panel">
            <p className="workspace-eyebrow">Monthly membership</p>
            <div className="membership-price">
              <strong>{money(price, data?.settings.pricing.currency)}</strong>
              {price > 0 && <span>/ month</span>}
            </div>
            <p>
              Your public profile is visible only after agency approval and
              active membership. Existing subscriptions keep their Stripe price
              when the agency updates future pricing.
            </p>
            <button
              className="workspace-primary"
              disabled={busy || !profile || profile.membership_status === "complimentary"}
              onClick={openBilling}
            >
              {profile?.membership_status === "complimentary"
                ? "Complimentary membership"
                : "Manage billing with Stripe"}
            </button>
          </section>
          <TalentEngagement events={data?.events || []} registrations={data?.registrations || []} notifications={data?.notifications || []} busy={busy} onAction={talentAction} />
        </aside>
      </div>
    </AppShell>
  );
}

function AgencyApplicationReview({
  application,
  busy,
  onReview,
}: {
  application: AgencyApplication;
  busy: boolean;
  onReview: (status: "reviewing" | "approved" | "rejected", notes: string) => void;
}) {
  const [notes, setNotes] = useState(application.admin_notes || "");
  useEffect(() => setNotes(application.admin_notes || ""), [application.admin_notes]);
  const locked = ["paid", "activated"].includes(application.status);
  return (
    <article className="agency-application-row">
      <header>
        <div>
          <small>{new Date(application.created_at * 1000).toLocaleDateString()} · {application.talent_type}</small>
          <h3>{application.applicant_name}</h3>
          <a href={`mailto:${application.applicant_email}`}>{application.applicant_email}</a>
          {application.applicant_phone && <span>{application.applicant_phone}</span>}
        </div>
        <Status value={application.status.replace(/_/g, " ")} />
      </header>
      <dl>
        {(application.city || application.state) && <div><dt>Location</dt><dd>{[application.city, application.state].filter(Boolean).join(", ")}</dd></div>}
        {application.age_range && <div><dt>Age range</dt><dd>{application.age_range}</dd></div>}
        {application.experience && <div><dt>Experience</dt><dd>{application.experience}</dd></div>}
        <div><dt>Why they want to join</dt><dd>{application.statement}</dd></div>
        {application.portfolio_url && <div><dt>Portfolio</dt><dd><a href={application.portfolio_url} target="_blank" rel="noreferrer">Open portfolio ↗</a></dd></div>}
      </dl>
      <label>
        Owner notes
        <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} disabled={locked} />
      </label>
      <div className="agency-review-actions">
        {!locked && <button disabled={busy} onClick={() => onReview("reviewing", notes)}>Mark reviewing</button>}
        {!locked && <button className="approve" disabled={busy} onClick={() => onReview("approved", notes)}>
          {["approved", "payment_pending"].includes(application.status) ? "Resend payment link" : "Approve & email payment link"}
        </button>}
        {!locked && <button className="reject" disabled={busy} onClick={() => onReview("rejected", notes)}>Reject</button>}
        {application.status === "paid" && <span>Payment confirmed · awaiting account activation</span>}
        {application.status === "activated" && <span>Account active</span>}
      </div>
    </article>
  );
}

function AdminDashboard({
  user,
  onLogout,
}: {
  user: User;
  onLogout: () => void;
}) {
  const [data, setData] = useState<AdminPlatformData | null>(null);
  const [tab, setTab] = useState<"talent" | "content" | "calendar" | "phase2" | "phase3">(
    "talent",
  );
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [eventForm, setEventForm] = useState<Partial<AgencyEvent>>({
    audience: "all",
    status: "published",
  });
  useEffect(() => {
    api
      .admin()
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);
  const counts = useMemo(
    () => ({
      total: data?.profiles.length || 0,
      pending:
        data?.profiles.filter((p) => p.public_status === "pending").length || 0,
      active:
        data?.profiles.filter((p) =>
          ["active", "complimentary"].includes(p.membership_status),
        ).length || 0,
      applications:
        data?.agency_applications.filter((application) =>
          ["new", "reviewing"].includes(application.status),
        ).length || 0,
    }),
    [data],
  );
  if (!data && !error) return <Loading />;
  async function act(body: Record<string, unknown>, success: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      setData(await api.adminAction(body));
      setNotice(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save changes.");
    } finally {
      setBusy(false);
    }
  }
  if (!data)
    return (
      <AppShell
        user={user}
        title="Agency administration"
        eyebrow="Action required"
        active="admin"
        onLogout={onLogout}
      >
        <div className="workspace-notice error">{error}</div>
      </AppShell>
    );
  const home = { ...FALLBACK_HOME, ...data.settings.homepage };
  return (
    <AppShell
      user={user}
      title="Agency administration"
      eyebrow="Content, talent, events, and opportunities"
      active="admin"
      onLogout={onLogout}
    >
      <div className="workspace-summary">
        <div>
          <span>Total talent</span>
          <strong>{counts.total}</strong>
        </div>
        <div>
          <span>Applications to review</span>
          <strong>{counts.applications}</strong>
        </div>
        <div>
          <span>Active members</span>
          <strong>{counts.active}</strong>
        </div>
      </div>
      <nav className="workspace-tabs">
        <button
          className={tab === "talent" ? "active" : ""}
          onClick={() => setTab("talent")}
        >
          Talent & pricing
        </button>
        <button
          className={tab === "content" ? "active" : ""}
          onClick={() => setTab("content")}
        >
          Homepage content
        </button>
        <button
          className={tab === "calendar" ? "active" : ""}
          onClick={() => setTab("calendar")}
        >
          Event calendar
        </button>
        <button
          className={tab === "phase2" ? "active" : ""}
          onClick={() => setTab("phase2")}
        >
          Sales & opportunities
        </button>
        <button className={tab === "phase3" ? "active" : ""} onClick={() => setTab("phase3")}>Operations & reports</button>
      </nav>
      {notice && <div className="workspace-notice success">{notice}</div>}
      {error && <div className="workspace-notice error">{error}</div>}
      {tab === "talent" && (
        <div className="admin-layout">
          <section className="workspace-panel agency-applications-admin">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Admission workflow</p>
                <h2>Agency applications</h2>
              </div>
              <Status value={`${counts.applications} awaiting review`} />
            </div>
            <p>Approving an application emails a private Stripe payment link. No account is created until payment succeeds and the applicant chooses a password.</p>
            {data.agency_applications.length ? (
              <div className="agency-application-list">
                {data.agency_applications.map((application) => (
                  <AgencyApplicationReview
                    key={application.id}
                    application={application}
                    busy={busy}
                    onReview={(status, adminNotes) =>
                      act(
                        {
                          action: "review-agency-application",
                          id: application.id,
                          status,
                          admin_notes: adminNotes,
                        },
                        status === "approved"
                          ? "Application approved and payment email sent."
                          : status === "rejected"
                            ? "Application rejected."
                            : "Application marked for review.",
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <Empty title="No agency applications yet" copy="New Model and Actor applications will appear here for owner review." />
            )}
          </section>
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Membership rates</p>
                <h2>New-member pricing</h2>
              </div>
            </div>
            <form
              className="pricing-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                act(
                  {
                    action: "update-settings",
                    model_price_cents: Math.round(
                      Number(form.get("model_price")) * 100,
                    ),
                    actor_price_cents: Math.round(
                      Number(form.get("actor_price")) * 100,
                    ),
                    grace_period_days: Number(form.get("grace_days")),
                    homepage: data.settings.homepage,
                  },
                  "Pricing updated for future checkouts.",
                );
              }}
            >
              <label>
                Model membership <span>$</span>
                <input
                  name="model_price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={(data.settings.pricing.model / 100).toFixed(2)}
                />
              </label>
              <label>
                Actor membership <span>$</span>
                <input
                  name="actor_price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={(data.settings.pricing.actor / 100).toFixed(2)}
                />
              </label>
              <label>
                Payment grace period{" "}
                <input
                  name="grace_days"
                  type="number"
                  min="0"
                  max="60"
                  defaultValue={data.settings.grace_period_days}
                />
                <span>days</span>
              </label>
              <p>
                Changes affect future checkouts. Existing Stripe subscriptions
                retain their agreed price unless intentionally changed in
                billing.
              </p>
              <button className="workspace-primary" disabled={busy}>
                Save pricing
              </button>
            </form>
          </section>
          <section className="workspace-panel talent-review">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Roster review</p>
                <h2>Talent accounts</h2>
              </div>
              <Status value={`${counts.pending} pending`} />
            </div>
            {data.profiles.length ? (
              <div className="admin-talent-list">
                {data.profiles.map((profile) => (
                  <article key={profile.user_id}>
                    <div
                      className="admin-avatar"
                      style={
                        profile.profile_photo_url
                          ? {
                              backgroundImage: `url(${profile.profile_photo_url})`,
                            }
                          : undefined
                      }
                    >
                      {profile.stage_name.slice(0, 1)}
                    </div>
                    <div>
                      <strong>{profile.stage_name}</strong>
                      <span>
                        {profile.talent_type} · {profile.email}
                      </span>
                    </div>
                    <label>
                      Profile
                      <select
                        value={profile.public_status}
                        onChange={(e) =>
                          act(
                            {
                              action: "review-profile",
                              user_id: profile.user_id,
                              public_status: e.target.value,
                              membership_status: profile.membership_status,
                            },
                            "Profile visibility updated.",
                          )
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="hidden">Hidden</option>
                        <option value="draft">Draft</option>
                      </select>
                    </label>
                    <label>
                      Membership
                      <select
                        value={profile.membership_status}
                        onChange={(e) =>
                          act(
                            {
                              action: "review-profile",
                              user_id: profile.user_id,
                              public_status: profile.public_status,
                              membership_status: e.target.value,
                            },
                            "Membership status updated.",
                          )
                        }
                      >
                        <option value="inactive">Inactive</option>
                        <option value="active">Active</option>
                        <option value="complimentary">Complimentary</option>
                        <option value="past_due">Past due</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </label>
                  </article>
                ))}
              </div>
            ) : (
              <Empty
                title="No talent accounts yet"
                copy="Approved applicants will appear here after membership payment and account activation."
              />
            )}
          </section>
        </div>
      )}
      {tab === "content" && (
        <section className="workspace-panel content-editor">
          <div className="panel-heading">
            <div>
              <p className="workspace-eyebrow">Homepage editor</p>
              <h2>Hero content</h2>
            </div>
            <span>Published immediately</span>
          </div>
          <form
            className="workspace-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              act(
                {
                  action: "update-settings",
                  homepage: Object.fromEntries(form.entries()),
                  model_price_cents: data.settings.pricing.model,
                  actor_price_cents: data.settings.pricing.actor,
                  grace_period_days: data.settings.grace_period_days,
                },
                "Homepage content published.",
              );
            }}
          >
            <div className="form-grid">
              <label>
                Eyebrow
                <input name="eyebrow" defaultValue={home.eyebrow} />
              </label>
              <label>
                Primary button
                <input name="primary_cta" defaultValue={home.primary_cta} />
              </label>
              <label className="full">
                Main headline
                <input name="title" defaultValue={home.title} />
              </label>
              <label className="full">
                Introduction
                <textarea name="intro" rows={4} defaultValue={home.intro} />
              </label>
              <label>
                Secondary button
                <input name="secondary_cta" defaultValue={home.secondary_cta} />
              </label>
            </div>
            <button className="workspace-primary" disabled={busy}>
              Publish homepage content
            </button>
          </form>
        </section>
      )}
      {tab === "calendar" && (
        <div className="admin-layout">
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Create or edit</p>
                <h2>Agency event</h2>
              </div>
            </div>
            <form
              className="workspace-form"
              onSubmit={(event) => {
                event.preventDefault();
                act(
                  { action: "save-event", ...eventForm },
                  "Agency calendar updated.",
                );
                setEventForm({ audience: "all", status: "published" });
              }}
            >
              <label>
                Event title
                <input
                  required
                  value={eventForm.title || ""}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, title: e.target.value })
                  }
                />
              </label>
              <div className="form-grid">
                <label>
                  Starts
                  <input
                    required
                    type="datetime-local"
                    value={eventForm.starts_at || ""}
                    onChange={(e) =>
                      setEventForm({ ...eventForm, starts_at: e.target.value })
                    }
                  />
                </label>
                <label>
                  Ends
                  <input
                    type="datetime-local"
                    value={eventForm.ends_at || ""}
                    onChange={(e) =>
                      setEventForm({ ...eventForm, ends_at: e.target.value })
                    }
                  />
                </label>
                <label>
                  Audience
                  <select
                    value={eventForm.audience || "all"}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        audience: e.target.value as AgencyEvent["audience"],
                      })
                    }
                  >
                    <option value="all">All talent</option>
                    <option value="models">Models</option>
                    <option value="actors">Actors</option>
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={eventForm.status || "published"}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        status: e.target.value as AgencyEvent["status"],
                      })
                    }
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </label>
                <label className="full">
                  Location
                  <input
                    value={eventForm.location || ""}
                    onChange={(e) =>
                      setEventForm({ ...eventForm, location: e.target.value })
                    }
                  />
                </label>
                <label className="full">
                  Description
                  <textarea
                    rows={4}
                    value={eventForm.description || ""}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        description: e.target.value,
                      })
                    }
                  />
                </label>
              </div>
              <button className="workspace-primary" disabled={busy}>
                {eventForm.id ? "Update event" : "Publish event"}
              </button>
            </form>
          </section>
          <section className="workspace-panel">
            <div className="panel-heading">
              <div>
                <p className="workspace-eyebrow">Schedule</p>
                <h2>All events</h2>
              </div>
            </div>
            {data.events.length ? (
              <div className="event-admin-list">
                {data.events.map((event) => (
                  <article key={event.id}>
                    <time>{dateLabel(event.starts_at)}</time>
                    <div>
                      <strong>{event.title}</strong>
                      <span>
                        {event.location || "No location"} · {event.audience}
                      </span>
                    </div>
                    <Status value={event.status} />
                    <button onClick={() => setEventForm(event)}>Edit</button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Send this event announcement to active talent?`)) {
                          act({ action: "notify-event", id: event.id }, "Event announcement sent to active talent.");
                        }
                      }}
                    >
                      Notify talent
                    </button>
                    <button
                      className="danger-link"
                      onClick={() =>
                        act(
                          { action: "delete-event", id: event.id },
                          "Event removed.",
                        )
                      }
                    >
                      Delete
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <Empty
                title="No events yet"
                copy="Create training, auditions, or showcases for the agency calendar."
              />
            )}
          </section>
        </div>
      )}
      {tab === "phase2" && <PhaseTwoAdmin data={data} busy={busy} act={act} />}
      {tab === "phase3" && <PhaseThreeAdmin data={data} busy={busy} act={act} />}
    </AppShell>
  );
}

function Footer() {
  return (
    <footer>
      <Brand compact />
      <p>Elite · Professional · Empowering</p>
      <p>© {new Date().getFullYear()} Paris Runway Elite Talent Agency</p>
    <p style={{ margin: "1rem 0 0", padding: "0 1rem", textAlign: "center", fontSize: "0.875rem", flexBasis: "100%" }}><a href="https://blissits.com" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: "0.2em" }}>Built by Bliss</a></p>
    </footer>
  );
}

export default function App() {
  const [path, setPath] = useState(route());
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(!getToken());
  useEffect(() => {
    const listener = () => setPath(route());
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);
  useEffect(() => {
    if (!getToken()) return;
    api
      .me()
      .then(setUser)
      .catch(() => api.logout())
      .finally(() => setAuthReady(true));
  }, []);
  useEffect(() => {
    const seo: Record<string, [string, string]> = {
      "/": ["Paris Runway Elite | Talent Agency", "Talent development, professional profiles, events, and opportunities in West Columbia, South Carolina."],
      "/talent": ["Talent Directory | Paris Runway Elite", "Explore agency-approved models and actors represented by Paris Runway Elite."],
      "/events": ["Events | Paris Runway Elite", "Explore Paris Runway Elite training, auditions, performances, tickets, vendor spaces, and sponsorships."],
      "/opportunities": ["Volunteer & Internship Opportunities | Paris Runway Elite", "Apply for volunteer and internship opportunities in talent, fashion, media, and events."],
      "/apply": ["Apply to Paris Runway Elite", "Submit a Model or Actor application for owner review before membership and account access."],
    };
    const base = path.startsWith("/talent/")
      ? seo["/talent"]
      : path.startsWith("/events/")
        ? seo["/events"]
        : path.startsWith("/opportunities/")
          ? seo["/opportunities"]
          : seo[path] || seo["/"];
    document.title = base[0];
    document.querySelector('meta[name="description"]')?.setAttribute("content", base[1]);
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", base[0]);
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", base[1]);
    const canonicalPath = path === "/register"
      ? "/apply"
      : ["/admin", "/dashboard", "/login", "/forgot-password", "/reset-password"].includes(path)
        ? "/"
        : path;
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `https://parisrunwayelite.com${canonicalPath === "/" ? "/" : canonicalPath}`);
    window.setTimeout(() => document.querySelector("main")?.setAttribute("id", "main-content"), 0);
  }, [path]);
  function logout() {
    api.logout();
    setUser(null);
    navigate("/");
  }
  if (!authReady) return <Loading />;
  if (path === "/login") return <AuthPage onAuth={setUser} />;
  if (path === "/forgot-password") return <PasswordRecoveryPage />;
  if (path === "/reset-password") return <PasswordRecoveryPage reset />;
  if (path === "/apply" || path === "/register") return <AgencyApplicationPage onAuth={setUser} />;
  if (path === "/dashboard")
    return user && !isAdmin(user) ? (
      <TalentDashboard user={user} onLogout={logout} />
    ) : (
      <AuthPage onAuth={setUser} />
    );
  if (path === "/admin")
    return user && isAdmin(user) ? (
      <AdminDashboard user={user} onLogout={logout} />
    ) : (
      <AuthPage onAuth={setUser} />
    );
  if (path === "/talent") return <Directory user={user} />;
  if (path.startsWith("/talent/"))
    return (
      <PublicProfile user={user} slug={decodeURIComponent(path.slice(8))} />
    );
  if (path === "/events") return <EventsPage user={user} />;
  if (path.startsWith("/events/"))
    return (
      <EventsPage user={user} eventId={decodeURIComponent(path.slice(8))} />
    );
  if (path === "/opportunities") return <OpportunitiesPage user={user} />;
  if (path.startsWith("/opportunities/"))
    return (
      <OpportunitiesPage
        user={user}
        opportunityId={decodeURIComponent(path.slice(15))}
      />
    );
  return <Home user={user} />;
}
